import axios, {AxiosRequestConfig} from "axios";
import axiosAsims from "../config/AxiosAsims";
import SurveyorCompletionStatus from "../types/SurveyorCompletionStatus";
import { AssetType } from "../types/AssetType";
import { ValidationType } from "../types/CheckList";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import { CarbonSectionData } from "../types/CarbonSection";
import AsimsApiResponse from "../types/AsimsApiResponse";
import Asset from "../types/Asset";
import {Inspection} from "../types/Inspection";
import {formatDate} from "../types/ReUseAsset";


export const requestAssetMetaData = (
    idToken: string,
    id: string,
    elementName: string,
    asimsSlot: string,
    assetType: string,
    isMainShipImage = false,
    validationType: ValidationType = ValidationType.NONE,
    assetUploadedBy: string,
    organizationId: number,
    asset: Asset,
    inspection: Inspection,
    surveyElementId: string,
    userSub: string | undefined
) => {
    return new Promise((req, reject) => {
        const sanitizedElementName = elementName.replace("’", "");
        let methodName = "upload-url";
        const metaData =
            "&metadata=asset-type,element-id,element-name,inspection-id,imo,inspection-created,inspection-number,asims-slot,asset-uploaded-by,cognito-sub";
        let endOfUrl = "";

        if (validationType === ValidationType.PDF_CHECKLIST) {
            methodName = "validate-document";
            endOfUrl += "&validation-type=surveyor-pdf";
        }

        endOfUrl += metaData;

        let uploadUrl = `/${methodName}?imo=${inspection.ShipIMO}&filename=${encodeURIComponent(
            asset.file.name
        )}${endOfUrl}`;

        const headers: any = {
            idtoken: idToken,
            "x-metadata-asset-type": assetType,
            "x-metadata-element-id": id,
            "x-metadata-element-name": sanitizedElementName,
            "x-metadata-inspection-id": inspection.Id.toLowerCase(),
            "x-metadata-imo": inspection.ShipIMO,
            "x-metadata-inspection-created": inspection.CreatedAt,
            "x-metadata-inspection-number": inspection.InspectionNumber,
            "x-metadata-asims-slot": asimsSlot,
            "x-metadata-asset-uploaded-by": assetUploadedBy,
            "x-metadata-cognito-sub": userSub
        };

        if (asset.reusable) {
            uploadUrl += ",reusable,reusable-expiry,org-id,survey-element-id";
            headers["x-metadata-org-id"] = organizationId;
            headers["x-metadata-reusable"] = "true";
            headers["x-metadata-reusable-expiry"] = asset.expiryTime ? formatDate(asset.expiryTime) : null;
            headers["x-metadata-survey-element-id"] = surveyElementId;
        }

        if (isMainShipImage) {
            uploadUrl += ",asims-image-type";
            headers["x-metadata-asims-image-type"] = "mainship";
        }

        ReactGA.event({
            category: "Upload file",
            action: "uploading",
            label: sanitizedElementName,
            value: 0
        });

        axiosAsims
            .get(uploadUrl, {
                headers: headers
            })
            .then((response) => {
                ReactGA.event({
                    category: "Upload file",
                    action: "complete",
                    label: sanitizedElementName,
                    value: 0
                });

                req(response.data.data);
            })
            .catch((err) => {
                toast.error(
                     asset.file.name +
                        " has failed to upload. Please check your connection, refresh the page and retry. If the problem persists, please contact operations@idwalmarine.com"
                );
                console.error(err);
                ReactGA.event({
                    category: "Upload file",
                    action: "failed",
                    label: sanitizedElementName,
                    value: 0
                });
                reject(err);
            });
    });
};

export const uploadAsset = (
    file: any,
    imo: string,
    idToken: string,
    id: string,
    guid: string,
    elementName: string,
    inspectionCreatedDate: string,
    inspectionNumber: string,
    assetType: string,
    assetId: string,
    putUrl: string,
    asimsSlot: string,
    onUploadProgress: any,
    isMainShipImage = false,
    metaDataCallback = "",
    userSub: string | undefined
) => {
    return new Promise((req, reject) => {
        const sanitizedElementName = elementName.replace("’", "");
        const headers: any = {
            "Content-Type": file.type,
            "x-amz-meta-asset-id": assetId,
            "x-amz-meta-asset-type": assetType,
            "x-amz-meta-element-id": id,
            "x-amz-meta-element-name": sanitizedElementName,
            "x-amz-meta-filename": encodeURIComponent(file.name),
            "x-amz-meta-inspection-id": guid.toLowerCase(),
            "x-amz-meta-imo": imo,
            "x-amz-meta-inspection-created": inspectionCreatedDate,
            "x-amz-meta-inspection-number": inspectionNumber,
            "x-metadata-asims-slot": asimsSlot,
            "x-amz-meta-cognito-sub": userSub
        };

        if (isMainShipImage) {
            headers["x-metadata-asims-image-type"] = "mainship";
        }

        if (metaDataCallback) {
            headers["x-metadata-callback"] = metaDataCallback;
            headers["x-metadata-validation-type"] = "surveyor-pdf";
        }

        axios
            .put(putUrl, file, {
                headers: headers,
                onUploadProgress
            })
            .then((response) => {
                req(response);
            })
            .catch((err) => {
                toast.error(
                    file.name +
                        " has failed to upload. Please check your connection, refresh the page and retry. If the problem persists, please contact operations@idwalmarine.com"
                );
                console.error(err);
                reject(err);
            });
    });
};

export const postSurveyorData = (
    surveyorCompletionStatus: SurveyorCompletionStatus,
    assetType: AssetType,
    invalidElements: string[],
    idToken: string,
    invalidReUseSections : string[]
) => {
    const options = {
        headers: {
            "Content-Type": "application/json",
            idtoken: idToken,
            "x-metadata-inspection-id":
                surveyorCompletionStatus.InspectionId.toLowerCase()
        }
    };
    return new Promise((req, reject) => {
        axiosAsims
            .post(
                "/surveyorcompletionstatus",
                {
                    surveyorCompletionStatus: surveyorCompletionStatus,
                    type: assetType,
                    invalidElements,
                    invalidReUseSections
                },
                options
            )
            .then((response) => {
                req(response.data.data);
            })
            .catch((err) => {
                console.error(err);
                reject(err);
            });
    });
};


/*
*
* Generic axios request function. Returns a resolved or rejected promise.
* @param {AxiosRequestConfig} config Request URL, method etc. Headers do not need to be included.
* @param {string} idtoken Cognito idtoken to access asims api.
* @return {any} Returns a resolved/ rejected promise.
*/
export const makeRequest = (
    config: AxiosRequestConfig,
    idtoken: string
): Promise<AsimsApiResponse> => {
    config.headers = {
        idtoken
    }

    return new Promise((req, reject) => {
        axiosAsims(config)
            .then((response) => {
                req(response.data);
            })
            .catch((err) => {
                console.error(err);
                reject(err);
            });
    });
}

export const postCarbonSection = (
    request: CarbonSectionData,
    idToken: string
) => {
    const options = {
        headers: {
            "Content-Type": "application/json",
            idtoken: idToken
        }
    };

    return new Promise((req, reject) => {
        axiosAsims
            .post(
                "/carbon-data",
                {
                    data: request
                },
                options
            )
            .then((response) => {
                req(response.data.data);
            })
            .catch((err) => {
                console.error(err);
                reject(err);
            });
    });
};
